import { mutationOptions } from '@tanstack/react-query';
import type { UseMutationOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { Logger } from '@/utils/logging';

import type {
  SubmitAppointmentRatingParams,
  SubmitAppointmentRatingRequest,
  SubmitAppointmentRatingResponse,
} from './types';

/**
 * Submit a rating for an appointment.
 *
 * @param params - The appointment ID, rating, and optional review
 * @returns The updated customer sale data
 */
const submitAppointmentRating = async ({
  appointmentId,
  appointmentRating,
  appointmentReview,
}: SubmitAppointmentRatingParams): Promise<SubmitAppointmentRatingResponse> => {
  try {
    const requestBody: SubmitAppointmentRatingRequest = {
      appointmentRating,
    };

    if (appointmentReview && appointmentReview.trim() !== '') {
      requestBody.appointmentReview = appointmentReview.trim();
    }

    const { data } = await apiClient.put<SubmitAppointmentRatingResponse>(
      `/v1/appointment/${appointmentId}/rating`,
      requestBody,
    );

    return data;
  } catch (error) {
    Logger.error('Error submitting appointment rating', { error });
    throw error;
  }
};

export const submitAppointmentRatingMutation = (
  options?: UseMutationOptions<
    SubmitAppointmentRatingResponse,
    Error,
    SubmitAppointmentRatingParams
  >,
) =>
  mutationOptions({
    mutationFn: (params: SubmitAppointmentRatingParams) =>
      submitAppointmentRating(params),
    mutationKey: ['submitAppointmentRating'],
    retry: false,
    ...options,
  });
